import { ref, watch, type Ref } from 'vue'
import type { CallbackDataParams } from 'echarts/types/dist/shared'
import type { ECElementEvent, EChartsType } from 'echarts/core'
import type { TooltipComponentOption } from 'echarts/components'
import type { ChartOptions } from '@/types/echart'
import {
    CHART_TOOLTIP_PIN_HOST_CLASS,
    CHART_TOOLTIP_PIN_HOST_PINNED_CLASS,
    TOOLTIP_PIN_ICON_CLASS,
    TOOLTIP_PIN_ICON_HTML
} from '@/constants/tooltipPin'

type TooltipState = Required<Pick<
    TooltipComponentOption,
    'triggerOn' | 'alwaysShowContent' | 'transitionDuration'
>>

type TooltipPinConfig = Omit<TooltipComponentOption, 'formatter'> & {
    formatter: Exclude<TooltipComponentOption['formatter'], string | undefined>
}

const DEFAULT_TOOLTIP_STATE: TooltipState = {
    triggerOn: 'mousemove|click',
    alwaysShowContent: false,
    transitionDuration: 0.4,
}

const SILENT = { silent: true }

export function adaptiveTooltipPosition (
    _point: number[],
    _params: unknown,
    _dom: HTMLElement | null,
    rect: { x: number; y: number; width: number; height: number } | null,
    size: { viewSize: number[]; contentSize: number[] }
): 'top' | 'bottom' {
    if (!rect) return 'top'
    const need = Math.max(size.contentSize[1], 1)
    const vh = Math.max(size.viewSize[1], 1)
    const above = rect.y
    const below = vh - rect.y - rect.height
    return above >= need ? 'top' : below >= need ? 'bottom' : above >= below ? 'top' : 'bottom'
}

function isSingleSeries (series: unknown): boolean {
    return !!series && typeof series === 'object' && !Array.isArray(series)
}

function getTooltipPinConfig (
    options?: ChartOptions
): TooltipPinConfig | null {
    if (!options || typeof options !== 'object') return null

    const tooltip = options.tooltip
    if (!tooltip || typeof tooltip !== 'object' || Array.isArray(tooltip)) return null

    const tooltipConfig = tooltip as TooltipPinConfig
    if (tooltipConfig.show === false || typeof tooltipConfig.formatter !== 'function') return null

    return tooltipConfig
}

/**
 * True when tooltip is shown (show !== false), primary config is an object, and formatter is a function — required for tooltip pin.
 */
export function hasTooltipFormatterFunction (options?: ChartOptions): boolean {
    return getTooltipPinConfig(options) !== null
}

export function hasAllowedSeriesTypes (
    options?: ChartOptions,
    allowedTypes: string[] = []
): boolean {
    const series = options?.series
    if (!series || allowedTypes.length === 0) return false
    const list = Array.isArray(series) ? series : [ series ]
    const allowedSet = new Set(allowedTypes)
    return list.some(item => isSingleSeries(item) && typeof item.type === 'string' && allowedSet.has(item.type))
}

export function prepareChartTooltipPinOptions (options: ChartOptions): ChartOptions {
    const base = getTooltipPinConfig(options)
    if (!base) return options

    const currentFormatter = base.formatter

    const existingClass = typeof base.className === 'string' ? base.className.trim() : ''
    const className = [ existingClass, CHART_TOOLTIP_PIN_HOST_CLASS ].filter(Boolean).join(' ')
    const position = typeof base.position === 'function' ? base.position : adaptiveTooltipPosition

    return {
        ...options,
        tooltip: {
            ...base,
            className,
            enterable: true,
            hideDelay: 400,
            appendToBody: false,
            confine: false,
            position,
            formatter: (params: CallbackDataParams | CallbackDataParams[], asyncTicket: string) => {
                const result = currentFormatter(params, asyncTicket)
                const content = result == null ? '' : String(result)
                if (!content || content.includes(TOOLTIP_PIN_ICON_CLASS)) {
                    return content
                }
                return `<div>${TOOLTIP_PIN_ICON_HTML} ${content}</div>`
            },
        },
    }
}

export function useChartTooltipPin (
    chartInstance: Ref<EChartsType | undefined>,
    enabled: Ref<boolean>
): void {
    const isPinned = ref(false)
    let savedTooltipState: TooltipState | null = null
    let showTipGeneration = 0

    function setTooltipHostPinned (chart: EChartsType, pinned: boolean): void {
        const host = chart.getDom().querySelector(`.${CHART_TOOLTIP_PIN_HOST_CLASS}`)
        host?.classList.toggle(CHART_TOOLTIP_PIN_HOST_PINNED_CLASS, pinned)
    }

    function isPointerOnTooltipHost (path: EventTarget[]): boolean {
        return path.some(
            node => node instanceof Element && node.closest(`.${CHART_TOOLTIP_PIN_HOST_CLASS}`)
        )
    }

    function readTooltipState (chart: EChartsType): TooltipState {
        const raw = chart.getOption().tooltip
        const tooltip = Array.isArray(raw) ? raw[0] : raw
        const data = tooltip && typeof tooltip === 'object' ? tooltip : {}

        return {
            triggerOn: typeof data.triggerOn === 'string'
                ? data.triggerOn
                : DEFAULT_TOOLTIP_STATE.triggerOn,
            alwaysShowContent: typeof data.alwaysShowContent === 'boolean'
                ? data.alwaysShowContent
                : DEFAULT_TOOLTIP_STATE.alwaysShowContent,
            transitionDuration: typeof data.transitionDuration === 'number'
                ? data.transitionDuration
                : DEFAULT_TOOLTIP_STATE.transitionDuration,
        }
    }

    function dismissPin () {
        if (!isPinned.value) return

        showTipGeneration++
        isPinned.value = false

        const restore = savedTooltipState ?? DEFAULT_TOOLTIP_STATE
        savedTooltipState = null

        const chart = chartInstance.value
        if (chart) {
            setTooltipHostPinned(chart, false)
            chart.setOption({ tooltip: restore }, SILENT)
            chart.dispatchAction({ type: 'hideTip' }, SILENT)
        }
    }

    function onChartClick (params: ECElementEvent) {
        const chart = chartInstance.value
        if (
            params.componentType !== 'series'
            || params.seriesIndex == null
            || params.dataIndex == null
            || !chart
        ) {
            return
        }

        if (!isPinned.value) {
            savedTooltipState = readTooltipState(chart)
        }
        isPinned.value = true

        chart.setOption(
            {
                tooltip: {
                    triggerOn: 'none',
                    alwaysShowContent: true,
                    enterable: true,
                    transitionDuration: 0,
                },
            },
            SILENT
        )

        showTipGeneration++
        const generation = showTipGeneration
        requestAnimationFrame(() => {
            const liveChart = chartInstance.value
            if (generation !== showTipGeneration || !liveChart || !isPinned.value) return

            liveChart.dispatchAction({ type: 'hideTip' }, SILENT)
            liveChart.dispatchAction(
                { type: 'showTip', seriesIndex: params.seriesIndex, dataIndex: params.dataIndex },
                SILENT
            )

            requestAnimationFrame(() => {
                const nextChart = chartInstance.value
                if (generation === showTipGeneration && nextChart && isPinned.value) {
                    setTooltipHostPinned(nextChart, true)
                }
            })
        })
    }

    function onDocumentPointerDown (event: PointerEvent) {
        if (isPinned.value && !isPointerOnTooltipHost(event.composedPath())) {
            dismissPin()
        }
    }

    watch(
        [ chartInstance, enabled ],
        ([ chart, isEnabled ], _prev, onCleanup) => {
            if (chart && isEnabled) {
                chart.on('click', onChartClick)
                document.addEventListener('pointerdown', onDocumentPointerDown, true)
                onCleanup(() => {
                    chart.off('click', onChartClick)
                    document.removeEventListener('pointerdown', onDocumentPointerDown, true)
                    dismissPin()
                })
                return
            }
        },
        { immediate: true }
    )
}